import { Layout } from '@sdk';
import { defineGlobal } from '@sdk/utils';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ViewType } from '@src/types/ViewType';
import { DockPointer } from './DockPointer';
import { NavigationActions } from './NavigationActions';
import { detectLayout, ROOT_PATH } from './url-builder';
import { isHomeSurface } from './dock-layout';

export interface UseDockNavigationReturn {
  /** Navigation actions instance */
  navigation: NavigationActions;

  /** Whether current URL is a dock URL */
  isDockUrl: boolean;

  /** Current dock pointer parsed from URL (null if not a dock URL) */
  currentDock: DockPointer | null;

  /**
   * True when the current URL is a `win/` focus-window URL
   * (docs/tab-management.md Part 3 §7). Derived read-only from the URL —
   * exactly like the `/dev/` detection above it. Nothing ever SETS window
   * mode; you navigate into it (deep-link and refresh work for free).
   */
  windowMode: boolean;
}

/**
 * Hook to access dock navigation functionality
 *
 * Returns navigation instance and current dock state from URL
 *
 * Uses relative navigation: NavigationActions reads current URL and replaces dock portion.
 * No need to track agentId/processId - they're preserved from the current URL.
 *
 * Usage:
 * ```tsx
 * const { navigation, isDockUrl, currentDock } = useDockNavigation();
 *
 * // Open a tab
 * navigation.openTab(ViewType.EDITOR);
 *
 * // Check if we're in a dock URL
 * if (isDockUrl) {
 *   console.log('Current dock:', currentDock?.viewType, currentDock?.pointer);
 * }
 * ```
 */
/**
 * Read-only selector for the current dock pointer parsed from the URL. Prefer this
 * over the full {@link useDockNavigation} when a component only needs to know *what*
 * is shown (e.g. which view type) and never navigates — it skips building a
 * NavigationActions instance and rewriting the `navigation` global on every URL change.
 */
export function useCurrentDock(): DockPointer | null {
  const location = useLocation();
  const params = useParams<{ viewType?: string }>();

  return useMemo(() => {
    const url = `${location.pathname}${location.search}`;
    if (params.viewType) {
      try {
        return DockPointer.fromUrl(url);
      } catch (error) {
        console.warn('[useDockNavigation] Invalid URL, returning default dock:', error);
        // Return default dock pointer for invalid URLs
        return new DockPointer();
      }
    }
    // The app root is an ordinary location, not the absence of one — so `/` and
    // its options (`?journeyId=`, `?highlight=`, `?scope-…`) are readable
    // through the pointer like anywhere else. This is what lets the param hooks
    // stop reaching for `useSearchParams` behind the pointer's back.
    if (location.pathname === ROOT_PATH) return DockPointer.root().withOptionsFromUrl(url);
    // Another top-level route (discover, an invite landing): genuinely not a
    // dock, and not the home either. Still null.
    return null;
  }, [location.pathname, location.search, params.viewType]);
}

/**
 * True when the current URL is a *home* surface in ANY view mode — the bare
 * home (no dock URL) or the HOME view (incl. the vibe `vibeNoProcess` landing)
 * — as opposed to a workspace or any other dock. This is the single predicate
 * for "is there no active session here worth preserving": consumed by
 * `flow-page` to pick the home hero and by the project-open flow to decide
 * whether switching a project should resume its last tab/build process (it
 * shouldn't, on home — home stays home, on the new project).
 */
export function useIsHomeSurface(): boolean {
  return isHomeSurface(useCurrentDock());
}

export function useDockNavigation(): UseDockNavigationReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const currentDock = useCurrentDock();

  // Create navigation instance with currentDock so openDock() can deduplicate
  const navigation = useMemo(() => {
    const actions = new NavigationActions(navigate, currentDock);
    defineGlobal('navigation', actions);
    return actions;
  }, [navigate, currentDock]);

  // "Is a dock open" — the ROOT is a location but not a dock, so it keeps
  // answering false here exactly as the old null did. Every consumer
  // (content-panel's body view type, NavigatorSlot, useActiveViewer) means
  // "something other than the home is showing".
  const isDockUrl = currentDock !== null && !currentDock.isRoot;

  // URL-derived, read-only (Part 3 §7): the focus-window layout is a property
  // of the URL, never of component state.
  const windowMode = detectLayout(location.pathname) === Layout.WIN;

  return {
    navigation,
    isDockUrl,
    currentDock,
    windowMode,
  };
}
